import React, {useMemo, useState, useEffect} from 'react';
import {View, Text, TouchableOpacity, FlatList, ScrollView} from 'react-native';
import {Colors, Typography} from 'src/styles';
import TimesheetStaff from 'src/modules/organization/components/timesheet-staff';
import TimesheetFilterMenu from '../timesheet-filter';
import {IconButton} from 'src/design-system';
import TimesheetClients from 'src/modules/organization/components/timesheet-clients';
import TimesheetSchool from 'src/modules/organization/components/timesheet-school';
import {exportCsv} from 'src/common-utils/export-csv';
import {appointmentLocations} from 'src/form-inputs/appointment-location-input';
import {IconButton as PaperIconButton} from 'react-native-paper';
import {compose} from 'recompose';
import {withDatabase} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {withObservables} from '@nozbe/watermelondb/react';
import {of, mergeMap} from 'rxjs';
import {Q} from '@nozbe/watermelondb';
import {useStyle} from 'src/providers/style';
import {useTranslations} from 'src/providers/translation';
import {statusArrayWithEvents} from 'src/hook-form-inputs/status-checkbox';
import {cptCodes} from 'src/hook-form-inputs/cpt';
import {Instance} from 'src/models';
import {useSessionDateSync} from 'src/hooks/useSessionDateSync';
import Spinner from 'react-native-loading-spinner-overlay';
import {useSelector, useDispatch} from 'react-redux';
import {setFilters} from 'src/redux/timesheet-filters';

const Timesheet = ({patients, users, instance}: any) => {
  const styles = useStyle();
  const translations = useTranslations();
  const {isLoading, syncDateRange} = useSessionDateSync();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [items, setItems] = useState([]);
  const {selectedFilters: filters} = useSelector(
    (state: any) => state.timesheetFilters,
  );

  const filtersWithDates = useMemo(
    () => ({
      ...filters,
      startDate: new Date(filters.startDate),
      endDate: new Date(filters.endDate),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.startDate,
      filters.endDate,
      filters.allTime,
      filters.staff,
      filters.clients,
      filters.locations,
      filters.types,
      filters.status,
    ],
  );

  const showSchoolTab =
    // Account number '77777' === Raven ABA Services dev instance
    instance?.accountNumber === '77777' || instance?.accountNumber === '6594';
  const [currentTab, setCurrentTab] = useState<string>(
    showSchoolTab ? 'SCHOOL' : 'STAFF',
  );

  const initiateCsvExport = async (name: string, content: string[][]) => {
    setLoading(true);
    await exportCsv(name.toLowerCase() + '_timesheet_export', content);
    setLoading(false);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
  };

  const getData = () => {
    let result = [];
    for (const key in filters) {
      if (Array.isArray(filters[key])) {
        result.push(...filters[key]);
      }
    }
    return result;
  };

  const deleteTag = (tag: any) => {
    let result: any = {};
    for (const key in filters) {
      if (Array.isArray(filters[key])) {
        result[key] = filters[key].filter((item: any) => item !== tag);
      } else {
        result[key] = filters[key];
      }
    }
    dispatch(setFilters(result));
  };

  const getName = (collection, tag) => {
    const selectedUser = collection.find(member => member.id === tag);
    return `${selectedUser.firstName} ${selectedUser.lastName}`;
  };

  // Sync sessions for the requested date range
  useEffect(() => {
    if (filtersWithDates?.startDate && filtersWithDates?.endDate) {
      syncDateRange(filtersWithDates.startDate, filtersWithDates.endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersWithDates?.startDate, filtersWithDates?.endDate]);

  return (
    <>
      <Spinner visible={isLoading} />
      <View style={[styles.row, styles.justifySpaceBetween, styles.padding]}>
        <View>
          <Text
            style={[
              Typography.H1,
              styles.row,
              styles.textAlignCenter,
              styles.paddingTop,
              styles.textColorPrimary,
            ]}>
            {translations('timesheet')}
          </Text>
        </View>
        <View
          style={[
            styles.flex,
            styles.row,
            styles.justifyEnd,
            styles.alignSelfCenter,
          ]}>
          <View style={[styles.paddingLRight]}>
            <IconButton
              loading={loading}
              disabled={loading || success}
              icon={success ? 'check' : 'download'}
              type={'outlined'}
              color={success ? Colors.SUCCESS : Colors.RAVEN_BLACK}
              onPress={() => initiateCsvExport(currentTab, items)}
            />
          </View>
          <TimesheetFilterMenu
            applyFilters={(newFilters: any) => dispatch(setFilters(newFilters))}
            filters={filters}
          />
        </View>
      </View>

      <View
        style={[styles.width, styles.paddingHorizontal, styles.paddingBottom]}>
        <ScrollView style={[styles.maxWidth]}>
          <FlatList
            horizontal
            data={getData()}
            renderItem={({item: selectedTag}) => (
              <View
                style={[
                  styles.backgroundColorBlack,
                  styles.textColorWhite,
                  styles.borderRadiusSM,
                  styles.marginSMVertical,
                  styles.marginLRight,
                  styles.row,
                ]}
                key={`filter-tag-key-${selectedTag}`}>
                <Text
                  style={[
                    Typography.P2,
                    styles.textColorWhite,
                    styles.alignSelfCenter,
                    styles.marginLHorizontal,
                  ]}>
                  {appointmentLocations.findIndex(
                    location => location.value === selectedTag,
                  ) !== -1
                    ? appointmentLocations[
                        appointmentLocations.findIndex(
                          location => location.value === selectedTag,
                        )
                      ].label
                    : cptCodes.findIndex(type => type.value === selectedTag) !==
                      -1
                    ? cptCodes[
                        cptCodes.findIndex(type => type.value === selectedTag)
                      ].label
                    : statusArrayWithEvents.findIndex(
                        type => type.value === selectedTag,
                      ) !== -1
                    ? statusArrayWithEvents[
                        statusArrayWithEvents.findIndex(
                          type => type.value === selectedTag,
                        )
                      ].label
                    : patients.findIndex(
                        patient => patient.id === selectedTag,
                      ) !== -1
                    ? getName(patients, selectedTag)
                    : getName(users, selectedTag)}
                </Text>
                <PaperIconButton
                  size={15}
                  icon="close"
                  color="white"
                  onPress={() => deleteTag(selectedTag)}
                />
              </View>
            )}
          />
        </ScrollView>
      </View>
      <View style={[styles.row, styles.paddingHorizontal]}>
        {showSchoolTab ? (
          <TouchableOpacity onPress={() => setCurrentTab('SCHOOL')}>
            <Text
              style={[
                styles.height40,
                styles.marginL,
                styles.justifyCenter,
                currentTab === 'SCHOOL' ? Typography.P3_BOLD : Typography.P3,
                currentTab === 'SCHOOL' ? styles.tabActive : {},
              ]}>
              {'School'}
            </Text>
          </TouchableOpacity>
        ) : (
          <>
            <TouchableOpacity onPress={() => setCurrentTab('STAFF')}>
              <Text
                style={[
                  styles.height40,
                  styles.marginL,
                  styles.justifyCenter,
                  currentTab === 'STAFF' ? Typography.P3_BOLD : Typography.P3,
                  currentTab === 'STAFF' ? styles.tabActive : {},
                ]}>
                {translations('staff')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setCurrentTab('CLIENT')}>
              <Text
                style={[
                  styles.height40,
                  styles.marginL,
                  styles.justifyCenter,
                  currentTab === 'CLIENT' ? Typography.P3_BOLD : Typography.P3,
                  currentTab === 'CLIENT' ? styles.tabActive : {},
                ]}>
                {translations('clients')}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>
      {currentTab === 'STAFF' ? (
        <TimesheetStaff
          filters={filtersWithDates}
          setTimesheetItems={setItems}
        />
      ) : currentTab === 'CLIENT' ? (
        <TimesheetClients
          filters={filtersWithDates}
          setTimesheetItems={setItems}
        />
      ) : (
        <TimesheetSchool
          filters={filtersWithDates}
          setTimesheetItems={setItems}
        />
      )}
    </>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['authentication'], ({database, authentication}: any) => ({
    profile: authentication.userId
      ? database.get('users').findAndObserve(authentication.userId)
      : of(),
    instance: database
      ?.get(Instance.table)
      .query(Q.where('_partition', authentication.selectedGroup))
      .observe()
      .pipe(mergeMap(x => x)),
  })),
  withObservables([], ({profile}: any) => {
    return {
      role: profile.role,
    };
  }),
  withObservables([], ({database, role, profile, authentication}: any) => {
    return {
      patients: !role?.patientAssignedOnly
        ? database
            .get('patients')
            .query(
              Q.where('deleted_at', null),
              Q.where('_partition', authentication.selectedGroup),
            )
        : profile.patients,
      users: database
        .get('users')
        .query(
          Q.where('deleted_at', null),
          Q.where('_partition', authentication.selectedGroup),
        )
        .observe(),
    };
  }),
)(Timesheet);
